//Gallery/GalleryMobile.tsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import {PostSection} from "./GalleryUIMobile";
import type {MediaEntry, Post} from "./GalleryUIMobile";
import { useTranslation } from 'react-i18next';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import { FaBars } from 'react-icons/fa';

// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

// ✅ bulletproof normalization
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

// ---- Asset prefetch utilities ----
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm)$/i;
const isVideoUrl = (u: string) => {
  try {
    const pathname = new URL(u, window.location.origin).pathname.toLowerCase();
    return (
      VIDEO_EXT_RE.test(pathname) ||
      pathname.endsWith('.m3u8') ||
      pathname.includes('/hls/') ||
      pathname.includes('/playlist/')
    );
  } catch {
    const s = u.toLowerCase();
    return (
      VIDEO_EXT_RE.test(s) ||
      s.endsWith('.m3u8') ||
      s.includes('/hls/') ||
      s.includes('/playlist/')
    );
  }
};

const IMAGE_BASE = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_BASE) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const toImageUrl = (p: string): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;

  const base = IMAGE_BASE.replace(/\/$/, '');
  const rel  = p.replace(/^\//, '');
  return `${base}/${rel}`;
};

// Idle scheduler so we don't block the main thread
const scheduleIdle = (fn: () => void) => {
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn, { timeout: 1000 });
  } else {
    setTimeout(fn, 0);
  }
};

//PREFETCH
const prefetchHlsPlaylist = async (videoId: string) => {
  const url = `${API_ORIGIN}/hls/playlist/${videoId}`;

  try {
    await fetch(url, { cache: 'force-cache' });

    // Warm first frame
    const v = document.createElement('video');
    v.src = url;
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;
    await v.play().catch(() => {});
    v.pause();
  } catch {}
};


// How aggressively we prefetch
const PREFETCH_POSTS_AHEAD = 3;          // ← preload ~5 posts ahead
const PREFETCH_MEDIA_PER_POST = 2;       // ← first N media per post (tune if needed)

const GALLERY_HARD_RELOAD_KEY = 'gallery:hardReload';
const G_SCROLL = 'gallery:scrollTop';
const G_INDEX  = 'gallery:pageIndex';
const G_OFFSET = 'gallery:offset';
const G_POSTS  = 'gallery:posts';    // session snapshot (we'll cap size)
const SNAPSHOT_POST_LIMIT = 30;      // don’t blow up sessionStorage

// Limiting Memory
const WINDOW_BEFORE = 1;
const WINDOW_AFTER = 2;
const MAX_POSTS_IN_MEMORY = 60;


// Optional: keep image handles bounded to avoid unbounded memory
const MAX_IMAGE_HANDLES = 120;
const trimImageHandles = () => {
  if (liveImageHandles.size <= MAX_IMAGE_HANDLES) return;
  // drop oldest ~50
  const drop = Math.min(50, liveImageHandles.size);
  const it = liveImageHandles.keys();
  for (let i = 0; i < drop; i++) {
    const k = it.next().value;
    if (k) liveImageHandles.delete(k);
  }
};

//Impression measurement for Advertising
const firedImpressions = new Set<string>();

const TrackAdImpression: React.FC<{ adId: string; isActive: boolean }> = ({ adId, isActive }) => {
  useEffect(() => {
    if (!isActive) return;
    if (firedImpressions.has(adId)) return;

    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled) return;
      if (!isActive) return;

      firedImpressions.add(adId);

      api.post(`/ads/${adId}/impression`).catch((err) =>
        console.error("❌ Impression tracking failed", err)
      );
    }, 1200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isActive, adId]);

  return null;
};


// Light network heuristic: don't prefetch on very slow/expensive links
const canPrefetch = () => {
  const nav = (navigator as any);
  const conn = nav?.connection || nav?.mozConnection || nav?.webkitConnection;
  const saveData = !!conn?.saveData;
  const et: string | undefined = conn?.effectiveType;
  // treat '2g', 'slow-2g' as too slow
  const verySlow = et === 'slow-2g' || et === '2g';
  return !saveData && !verySlow;
};

// In-memory cache so we don't prefetch the same URL repeatedly
const prefetched = new Set<string>();

/**
 * Prefetch an image/video. For images we keep a reference to avoid premature GC.
 * For videos we create a metadata-only video element.
 */
const liveImageHandles = new Map<string, HTMLImageElement>();

const prefetchAsset = (url?: string) => {
  if (!url || prefetched.has(url) || !canPrefetch()) return;

  if (isVideoUrl(url)) {
    const v = document.createElement('video');
    v.preload = 'metadata';        // keep it light
    v.src = url;
    // Kick load on iOS/Safari
    try { v.load(); } catch {}
  } else {
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'lazy';
    img.src = url;
    liveImageHandles.set(url, img);
    // When it settles, we can drop the handle
    const cleanup = () => liveImageHandles.delete(url);
    img.onload = cleanup;
    img.onerror = cleanup;
  }

  // Optional <link rel="prefetch"> to hint the browser (mostly for images)
  try {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
    // don't bother tracking/removing; browsers ignore duplicates
  } catch {}

  prefetched.add(url);
};

const GalleryMobile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [likingInProgress, setLikingInProgress] = useState<string | null>(null);
  const [offset, setOffset] = useState<number>(() => {
    const v = Number(sessionStorage.getItem(G_OFFSET));
    return Number.isFinite(v) ? v : 0;
  });
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 6; // You can tune this for performance
  const { likedIds, likeCounts, likePost, unlikePost, refreshLikes } = useLikes();

  const [posts, setPosts] = useState<Post[]>(() => {
    try {
      const raw = sessionStorage.getItem(G_POSTS);
      const snap = raw ? JSON.parse(raw) : null;
      return Array.isArray(snap) ? snap : [];
    } catch {
      return [];
    }
  });
  const [isFetching, setIsFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const loadingLabel = "Loading posts…";
  const isInitialLoading = isFetching && posts.length === 0;

const [snapDisabled, setSnapDisabled] = useState<boolean>(false);
const rrLoc = useRouterLocation();

//Search Filter Helper
// Normalize/prepare a string for search
const normText = (s?: string | null) => (s || '').toLowerCase();

// Does this post match the current search?
const matchesSearch = (post: Post, rawQuery: string): boolean => {
  const q = rawQuery.trim();
  if (!q) return true; // empty search → no filtering

  // remove leading '#' so both "#budapest" and "budapest" work
  const needle = q.replace(/^#/, '').toLowerCase();

  // Collect searchable fields
  const haystack: string[] = [];

  // Caption / description fields (adjust names if needed)
  if ((post as any).caption) haystack.push((post as any).caption);
  if ((post as any).description) haystack.push((post as any).description);

  // Hashtags stored as an array? e.g. ["budapest", "nightlife"]
  if (Array.isArray((post as any).hashtags)) {
    haystack.push(...(post as any).hashtags);
  }
  if (Array.isArray((post as any).tags)) {
    haystack.push(...(post as any).tags);
  }

  // Also allow city / country to match
  if (post.location?.city) haystack.push(post.location.city);
  if (post.location?.country) haystack.push(post.location.country);

  // Substring, case-insensitive: "pest" matches "Budapest"
  return haystack.some((txt) =>
    txt && normText(txt).includes(needle)
  );
};


const handleMenuClick = () => {
    navigate('/menu');  // Navigate to the menu page
  };

  
// in Gallery component, near the top of the function, BEFORE useMediaViewerOverlay:
const handleOpenMusic = async (entry: MediaEntry) => {
  // 1️⃣ Best case: we already stored a canonical track id
  if (entry.musicId) {
    navigate(`/musicdetails/${entry.musicId}`);
    return;
  }

  // 2️⃣ Fallback: search by title OR artist (not "title artist")
  const q = (entry.musicTitle || entry.musicArtist || '').trim();
  if (!q) {
    console.warn('[handleOpenMusic] no musicTitle/musicArtist on entry', entry);
    return;
  }

  try {
    const res = await api.get('/music', { params: { q } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);

    if (!list.length) {
      console.warn('[handleOpenMusic] no matching track for query', q, entry);
      return;
    }

    navigate(`/musicdetails/${list[0]._id}`);
  } catch (e) {
    console.error('[handleOpenMusic] Music lookup failed:', e);
  }
};

const filteredPosts = React.useMemo(
  () => posts.filter((p) => matchesSearch(p, searchQuery)),
  [posts, searchQuery]
);


const saveGallerySnapshot = () => {
  try {
    const top = feedRef.current?.scrollTop ?? 0;
    sessionStorage.setItem(G_SCROLL, String(top));
    sessionStorage.setItem(G_INDEX, String(pageIndex));
    sessionStorage.setItem(G_OFFSET, String(offset));

    const snap = posts.slice(0, SNAPSHOT_POST_LIMIT);
    sessionStorage.setItem(G_POSTS, JSON.stringify(snap));

    const anchorId = filteredPosts[pageIndex]?._id;
    if (anchorId) sessionStorage.setItem('gallery:anchorId', anchorId);
  } catch {}
};

const feedRef = useRef<HTMLDivElement | null>(null);

//Loading
const lastIndexRef = useRef(0);
const prefetchGenRef = useRef(0);
const [scrollDir, setScrollDir] = useState<'up' | 'down'>('down');




//Sorting
const createdAtTime = (p: { createdAt: string }) => {
  const t = Date.parse(p.createdAt);
  return Number.isFinite(t) ? t : 0;
};

const sortByDateDesc = (a: { createdAt: string }, b: { createdAt: string }) =>
  createdAtTime(b) - createdAtTime(a);



const buildMediaParams = (limit?: number, offset?: number) => {
  const params: Record<string, any> = {};
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  return params;
};


const asMediaEntry = (m: any): MediaEntry | null => {
  if (!m) return null;

  console.log("Processing media entry: ", m);

  // ✅ HLS VIDEO — DO NOT CREATE URL
  if (m?.isHls === true || m?.videoId || m?.hlsUrl || m?.dashUrl) {
  return {
    isHls: true,
    videoId: m.videoId ?? null,
    hlsUrl: m.hlsUrl ?? m.url ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? null,
    musicId: m.musicId ?? m.music?._id ?? null,
    musicTitle: m.musicTitle ?? m.music?.title ?? null,
    musicArtist: m.musicArtist ?? m.music?.artist ?? null,
  };
}

  // ✅ IMAGE / NON-HLS VIDEO
  const raw = m.url ?? m.src ?? m.path ?? m.file ?? null;
if (!raw || typeof raw !== 'string') return null;

return {
  url: toImageUrl(raw),
  musicId: m.musicId ?? m.music?._id ?? m.audio?._id ?? null,
  musicTitle: m.musicTitle ?? m.songTitle ?? m.trackTitle ?? m.music?.title ?? m.audio?.title ?? null,
  musicArtist: m.musicArtist ?? m.songArtist ?? m.trackArtist ?? m.music?.artist ?? m.audio?.artist ?? null,
};
};


const formatMediaArray = (arr: any[]): MediaEntry[] =>
  (Array.isArray(arr) ? arr : []).map(asMediaEntry).filter(Boolean) as MediaEntry[];

const [globalMuted, setGlobalMuted] = useState<boolean>(true); // start muted

const getMediaUrl = toImageUrl;

const [pageIndex, setPageIndex] = useState<number>(() => {
  const v = Number(sessionStorage.getItem(G_INDEX));
  return Number.isFinite(v) ? v : 0;
});


useEffect(() => {
  if (pageIndex > lastIndexRef.current) setScrollDir('down');
  else if (pageIndex < lastIndexRef.current) setScrollDir('up');

  lastIndexRef.current = pageIndex;
}, [pageIndex]);


const warmMediaCache = async () => {
  try {
    const params = buildMediaParams(4, 0);
    const res = await api.get('/media', { params });

    const data = Array.isArray(res.data?.posts) ? res.data.posts
               : Array.isArray(res.data) ? res.data : [];

    const normalized = data.map((p: any) => ({
  ...p,
  _id: p._id,
  likeKey: likeKeyFromRow(p),
  media: formatMediaArray(p.media),
  user: p.user
    ? { ...p.user, picture: p.user?.picture ? getMediaUrl(p.user.picture) : null }
    : null,
}));

const ordered = preserveAdOrder(normalized);

// hydrate only if we don't already have posts
setPosts(prev => prev.length ? prev : ordered);

  } catch (err) {
    console.error('❌ warmMediaCache failed:', err);
  }
};



const resumeGuardRef = useRef(false);

const anchoredOnceRef = React.useRef(false);

//Refresh Likes
useEffect(() => {
  // Every time we *enter* this page, sync likes from backend
  refreshLikes();
}, [refreshLikes]);

// Keyboard: swipe one post per key press (Down/Up, PgDn/PgUp, J/K, Space)
useEffect(() => {
  const el = feedRef.current;
  if (!el) return;

  const isTypingTarget = (t: EventTarget | null) => {
    const n = t as HTMLElement | null;
    if (!n) return false;
    const tag = n.tagName;
    return (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      (n as HTMLElement).isContentEditable
    );
  };

  const getIndex = () =>
    Math.round((el.scrollTop || 0) / (el.clientHeight || 1));

  const scrollToIndex = (i: number) => {
    const max = Math.max(0, filteredPosts.length - 1);
    const idx = Math.max(0, Math.min(max, i));
    el.scrollTo({ top: idx * (el.clientHeight || 1), behavior: 'smooth' });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    // don't hijack typing in fields or editable areas
    if (isTypingTarget(e.target)) return;

    // if no posts, ignore
    if (!filteredPosts.length) return;

    const idx = getIndex();

    // Down-like keys: ArrowDown, PageDown, J/j, Space (unless Shift+Space)
    if (
      e.key === 'ArrowDown' ||
      e.key === 'PageDown' ||
      e.key === 'j' ||
      e.key === 'J' ||
      (e.key === ' ' && !e.shiftKey)
    ) {
      e.preventDefault();
      scrollToIndex(idx + 1);
      return;
    }

    // Up-like keys: ArrowUp, PageUp, K/k, Shift+Space
    if (
      e.key === 'ArrowUp' ||
      e.key === 'PageUp' ||
      e.key === 'k' ||
      e.key === 'K' ||
      (e.key === ' ' && e.shiftKey)
    ) {
      e.preventDefault();
      scrollToIndex(idx - 1);
      return;
    }

    // Home / End jump
    if (e.key === 'Home') {
      e.preventDefault();
      scrollToIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      scrollToIndex(filteredPosts.length - 1);
      return;
    }
  };

  window.addEventListener('keydown', onKeyDown, { passive: false });
  return () => window.removeEventListener('keydown', onKeyDown);
}, [filteredPosts.length]);


useEffect(() => {
  if (anchoredOnceRef.current) return;
  const postId = sessionStorage.getItem('galleryRestorePostId');
  if (!postId || !feedRef.current) return;

  const container = feedRef.current;

  // try immediate
  const tryAnchor = () => {
    const el = container.querySelector<HTMLElement>(`[data-post-id="${postId}"]`);
    if (!el) return false;
    // Let the browser scroll the nearest scrollable ancestor (the container)
    // Scroll-snap will naturally align it to the page without us disabling anything.
    el.scrollIntoView({ block: 'start', inline: 'nearest' }); // behavior defaults to 'auto'
    anchoredOnceRef.current = true;
    sessionStorage.removeItem('galleryRestorePostId');
    return true;
  };

  if (tryAnchor()) return;

  // Retry briefly while items paginate/render in
  let attempts = 0;
  const t = setInterval(() => {
    attempts += 1;
    if (tryAnchor() || attempts >= 20) clearInterval(t); // ~2s @ 100ms
  }, 100);
  return () => clearInterval(t);
}, [posts]); // depend on posts so we retry as they load

// Prefetch a window of upcoming posts (5 ahead by default)
useEffect(() => {
  if (!canPrefetch()) return;
  if (!filteredPosts.length) return;

  const start =
  scrollDir === 'down'
    ? pageIndex
    : Math.max(0, pageIndex - PREFETCH_POSTS_AHEAD);

const end =
  scrollDir === 'down'
    ? Math.min(filteredPosts.length, pageIndex + PREFETCH_POSTS_AHEAD)
    : pageIndex + 1;

  const queue: string[] = [];

  for (let i = start; i < end; i++) {
    const post = filteredPosts[i];
    if (!post) continue;

    // 👤 author avatar
    if (post.user?.picture) queue.push(post.user.picture);

    // 🎞 media
    const mediaArr = post.media || [];
    const N = Math.min(PREFETCH_MEDIA_PER_POST, mediaArr.length);

    for (let j = 0; j < N; j++) {
      const media = mediaArr[j];

      // 🔥 HLS video → prefetch playlist
      if (media?.isHls && media.videoId) {
        prefetchHlsPlaylist(media.videoId);
        continue;
      }

      // 🖼 image / mp4
      if (media?.url) {
        queue.push(media.url);
      }
    }
  }
const gen = ++prefetchGenRef.current;

  scheduleIdle(() => {
  if (gen !== prefetchGenRef.current) return;
  for (const u of queue) prefetchAsset(u);
  trimImageHandles();

  });
}, [pageIndex, filteredPosts]);


// Warm-up when list first appears
useEffect(() => {
  if (!canPrefetch()) return;
  if (!filteredPosts.length) return;

  const firstWindow = Math.min(
    filteredPosts.length,
    PREFETCH_POSTS_AHEAD
  );

  const queue: string[] = [];

  for (let i = 0; i < firstWindow; i++) {
    const post = filteredPosts[i];
    if (!post) continue;

    // 👤 avatar
    if (post.user?.picture) queue.push(post.user.picture);

    // 🎞 media
    const mediaArr = post.media || [];
    const N = Math.min(PREFETCH_MEDIA_PER_POST, mediaArr.length);

    for (let j = 0; j < N; j++) {
      const media = mediaArr[j];

      // 🔥 HLS video → prefetch playlist
      if (media?.isHls && media.videoId) {
        prefetchHlsPlaylist(media.videoId);
        continue;
      }

      // 🖼 image / mp4
      if (media?.url) {
        queue.push(media.url);
      }
    }
  }
const gen = ++prefetchGenRef.current;

  scheduleIdle(() => {
      if (gen !== prefetchGenRef.current) return;
    for (const u of queue) prefetchAsset(u);
    trimImageHandles();
  });
}, [filteredPosts.length]);

// call once on mount / location change
useEffect(() => {
  const params = new URLSearchParams(rrLoc.search);
  const hasRefreshParam = params.has('refresh');
  const hardReloadFlag = sessionStorage.getItem(GALLERY_HARD_RELOAD_KEY) === '1';

  const shouldHardReload = hardReloadFlag || hasRefreshParam;

  if (shouldHardReload) {
    sessionStorage.removeItem(GALLERY_HARD_RELOAD_KEY);
    sessionStorage.removeItem(G_SCROLL);
    sessionStorage.removeItem(G_INDEX);
    sessionStorage.removeItem(G_OFFSET);
    sessionStorage.removeItem(G_POSTS);
    sessionStorage.removeItem('gallery:anchorId');
    sessionStorage.removeItem('galleryRestorePostId');

    setPageIndex(0);
    setOffset(0);
    setHasMore(true);
    setPosts([]);
    setIsFetching(false); // optional extra safety
    fetchPosts(0, true);
  } else {
    warmMediaCache();
    if (!posts.length && !isFetching) {
      setHasMore(true);
      fetchPosts(0);
    }
  }

  const id = setInterval(warmMediaCache, 30_000);
  return () => clearInterval(id);
}, [rrLoc.search]);


// 2a) Only one <video> plays at a time
useEffect(() => {
  const handlePlay = (e: Event) => {
    const target = e.target as HTMLVideoElement | null;
    if (!target || target.tagName !== 'VIDEO') return;
    document.querySelectorAll('video').forEach((v) => {
      if (v !== target) {
        try { v.pause(); } catch {/* ignore */}
      }
    });
  };
  document.addEventListener('play', handlePlay, true); // capture phase
  return () => document.removeEventListener('play', handlePlay, true);
}, []);


// 2b) Disable autoplay everywhere and pause fresh mounts

//Loading
useEffect(() => {
  const style = document.createElement('style');
  style.innerHTML = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  `;
  document.head.appendChild(style);

  return () => {
    document.head.removeChild(style);
  };
}, []);


// Search effect
useEffect(() => {
  // whenever the search changes, jump back to the top of the filtered list
  setPageIndex(0);
  if (feedRef.current) {
    feedRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }
}, [searchQuery]);


const preserveAdOrder = (arr: Post[]) => {
  const hasAds = arr.some(p => p?.isAd);
  return hasAds ? arr.slice() : [...arr].sort(sortByDateDesc);
};

  // Fetch Posts and location   
  const fetchPosts = async (startOffset = 0, force = false) => {
    if (!force && isFetching) return;
  setIsFetching(true);
  try {
    const params = buildMediaParams(LIMIT, startOffset);
    const res = await api.get('/media', { params });
    const data = res.data.posts || res.data;

    const transformed: Post[] = data.map((row: any) => {
      const likeKey = likeKeyFromRow(row);  // canonical id
    
      return {
        ...row,
        _id: row._id,        // keep original Mongo id
        likeKey,             // 👈 add this
        media: formatMediaArray(row.media),
        likedByUser:
          user && user._id && Array.isArray(row.likesBy)
            ? row.likesBy.some((id: any) => id.toString() === user._id)
            : false,
        user: row.user
          ? {
              _id: row.user?._id || '',
              profilename: row.user?.profilename || t('gallery.unknownUser'),
              picture: row.user?.picture ? toImageUrl(row.user.picture) : null
            }
          : null,
        location: {
          city: row.location?.city || t('gallery.unknownLocation'),
          country: row.location?.country || t('gallery.unknownLocation'),
        },
      };
    });

const sorted = preserveAdOrder(transformed);

setPosts(prev => {
  const existingIds = new Set(prev.map(p => p._id));
  const newOnes = sorted.filter(p => !existingIds.has(p._id));
  const merged =
    startOffset === 0 && !prev.length
      ? sorted
      : [...prev, ...newOnes];

  return preserveAdOrder(merged).slice(-MAX_POSTS_IN_MEMORY);
});

    
    setOffset(startOffset + LIMIT);
    setHasMore(res.data.hasMore !== false);
  } catch (err) {
    console.error('Failed to fetch paginated posts:', err);
  } finally {
    setIsFetching(false);
  }
};

useEffect(() => {
  if (resumeGuardRef.current) return; // skip once right after resume
  if (posts.length && pageIndex >= posts.length - 2 && hasMore && !isFetching) {
  fetchPosts(offset);
  }
}, [pageIndex, posts.length, hasMore, isFetching, offset]);

   // LIKE
   const handleLike = async (postKey: string) => {
    const id = String(postKey);
    if (likingInProgress === id) return;
    setLikingInProgress(id);
  
    try {
      const newLikes = await likePost(id);
  
      if (newLikes === undefined) {
        setPosts(prev =>
          prev.map(p => {
            const key = String((p as any).likeKey || p._id);
            return key === id ? { ...p, likedByUser: true } : p;
          })
        );
        return;
      }
  
      setPosts(prev =>
        prev.map(p => {
          const key = String((p as any).likeKey || p._id);
          return key === id ? { ...p, likedByUser: true, likes: newLikes } : p;
        })
      );
    } catch (err) {
      console.error('Like failed:', err);
    } finally {
      setLikingInProgress(null);
    }
  };
  
  const handleUnlike = async (postKey: string) => {
    const id = String(postKey);
    if (likingInProgress === id) return;
    setLikingInProgress(id);
  
    try {
      const newLikes = await unlikePost(id);
  
      if (newLikes === undefined) {
        setPosts(prev =>
          prev.map(p => {
            const key = String((p as any).likeKey || p._id);
            return key === id ? { ...p, likedByUser: false } : p;
          })
        );
        return;
      }
  
      setPosts(prev =>
        prev.map(p => {
          const key = String((p as any).likeKey || p._id);
          return key === id ? { ...p, likedByUser: false, likes: newLikes } : p;
        })
      );
    } catch (err) {
      console.error('Unlike failed:', err);
    } finally {
      setLikingInProgress(null);
    }
  };
  

//Mute
// optional: persist across reloads
useEffect(() => {
  const saved = localStorage.getItem('globalMuted');
  if (saved != null) setGlobalMuted(JSON.parse(saved));
}, []);
useEffect(() => {
  localStorage.setItem('globalMuted', JSON.stringify(globalMuted));
}, [globalMuted]);


useEffect(() => {
  if (!posts.length) return;

  setPosts(prev =>
    prev.map(p => {
      const key = String((p as any).likeKey || p._id);
      const count = likeCounts.get(key);   // 👈 might be undefined

      return {
        ...p,
        likedByUser: likedIds.has(key),
        likes:
  count != null
    ? Math.max(count, p.likes ?? 0)
    : p.likes,

      };
    })
  );
}, [likedIds, likeCounts, posts.length]);


useEffect(() => {
  return () => {
    saveGallerySnapshot(); // when leaving Gallery
  };
}, [pageIndex, offset, posts]);

useEffect(() => {
  if (sessionStorage.getItem(GALLERY_HARD_RELOAD_KEY) === '1') return; // cold load path
  // we plan to resume → skip one auto-fetch cycle
  resumeGuardRef.current = true;
  queueMicrotask(() => { resumeGuardRef.current = false; });
}, []);

// Restore once posts are present
useEffect(() => {
  if (sessionStorage.getItem(GALLERY_HARD_RELOAD_KEY) === '1') return; // cold path skips

  const el = feedRef.current;
  if (!el) return;
  if (!posts.length) return;

  const anchorId = sessionStorage.getItem('gallery:anchorId');
  const topStr   = sessionStorage.getItem(G_SCROLL);

  let attempts = 0;
  setSnapDisabled(true);

  const tryRestore = () => {
    attempts += 1;

    // Prefer anchor id (more robust than raw scrollTop)
    if (anchorId) {
      const target = el.querySelector<HTMLElement>(`[data-post-id="${anchorId}"]`);
      if (target) {
        target.scrollIntoView({ block: 'start', inline: 'nearest' });
        // leave snap disabled for one more frame to avoid snap interference
        requestAnimationFrame(() => setSnapDisabled(false));
        return;
      }
    }

    // Fallback: absolute pixel restore
    if (topStr) {
      el.scrollTo({ top: Number(topStr), behavior: 'auto' });
      requestAnimationFrame(() => setSnapDisabled(false));
      return;
    }

    // Retry a few times while images/layout settle
    if (attempts < 15) {
      setTimeout(tryRestore, 50);
    } else {
      setSnapDisabled(false);
    }
  };

  // kick off after one frame so children are measured
  requestAnimationFrame(tryRestore);
}, [posts.length]);

useEffect(() => {
  const onVisibility = () => {
    if (document.visibilityState !== "visible") {
      prefetched.clear();
      liveImageHandles.clear();
    }
  };

  document.addEventListener("visibilitychange", onVisibility);
  return () => document.removeEventListener("visibilitychange", onVisibility);
}, []);

useEffect(() => {
  return () => {
    document.querySelectorAll('video').forEach(v => {
      try {
        v.pause();
        v.removeAttribute('src');
        v.load();
      } catch {}
    });
  };
}, []);

// iOS Safari: warm video decoder once to prevent first-play lag
useEffect(() => {
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (!isIOS) return;

  const v = document.createElement('video');
  v.muted = true;
  v.playsInline = true;
  v.preload = 'auto';

  // tiny silent mp4 OR your shortest HLS playlist
  v.src = `${API_ORIGIN}/hls/decoder-warmup`; 
  // can be:
  // - a 1-second blank HLS
  // - or a tiny mp4

  const play = async () => {
    try {
      await v.play();
      v.pause();
    } catch {}
  };

  play();

  return () => {
    try {
      v.pause();
      v.removeAttribute('src');
      v.load();
    } catch {}
  };
}, []);


return (
<div style={{ display: 'flex', minHeight: '100dvh', overflow: 'hidden' }}>
      {/* Feed Content — vertical swipe per post + horizontal carousel inside */}
      <div
        ref={feedRef}
        onScroll={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          const idx = Math.round((el.scrollTop || 0) / (el.clientHeight || 1));
          if (idx !== pageIndex) setPageIndex(idx);

          // NEW: remember the post id we’re centered on
          const anchorId = filteredPosts[idx]?._id;
          if (anchorId) sessionStorage.setItem('gallery:anchorId', anchorId);
        }}
        style={{
          ...feedContainerStyle,
          padding: 7,
          scrollSnapType: snapDisabled ? 'none' : 'y mandatory',
        }}
      >
        {isInitialLoading ? (
  <div
    style={{
      height: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 14,
      color: '#bbb',
      fontSize: 16,
      letterSpacing: 0.4,
      paddingBottom: 40,
    }}
  >
    {/* Spinner */}
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />

    {/* Text */}
    <div style={{ opacity: 0.9 }}>
      {loadingLabel}
    </div>
  </div>
) : filteredPosts.length > 0 ? (

 filteredPosts.map((post, idx) => {
  const isActive = idx === pageIndex;
  const inWindow = idx >= pageIndex - 1 && idx <= pageIndex + 1;
  const shouldRender =
    idx >= pageIndex - WINDOW_BEFORE &&
    idx <= pageIndex + WINDOW_AFTER;

  const handleAdClick = () => {
    if (post.isAd) {
      api.post(`/ads/${post._id}/click`).catch((err) =>
        console.error("❌ Click tracking failed", err)
      );
    }
  };

  if (!shouldRender) {
    return (
      <section
        key={post._id}
        data-post-id={post._id}
        style={{
          minHeight: '100dvh',
          height: '100dvh',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
        }}
      />
    );
  }

  return (
    <section
      key={post._id}
      data-post-id={post._id}
      style={{
        minHeight: '100dvh',
        height: '100dvh',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
      }}
    >
      {post.isAd && (
        <TrackAdImpression
          adId={post._id}
          isActive={isActive}
        />
      )}

      <PostSection
        post={post}
        isActive={isActive}
        inWindow={inWindow}
        globalMuted={globalMuted}
        navigate={navigate}
        onLike={() => handleLike(post.likeKey || post._id)}
        onUnlike={() => handleUnlike(post.likeKey || post._id)}
        currentUserId={user?._id}
        onMusicClick={handleOpenMusic}
        onAdClick={handleAdClick}
      />
    </section>
  );
})
        ) : (
          <p style={{ color: '#bbb', padding: 16 }}>
            {t('gallery.noPostsForFilter')}
          </p>
        )}
      </div>

      {/* Add the Hamburger Menu Button on the right side */}
      <div 
        onClick={handleMenuClick} 
        style={{
          position: 'absolute',
          top: '10px', // Position it slightly from the top
          right: '17px', // Right from the edge
          backgroundColor: 'transparent', // Optional: dark background for contrast
          borderRadius: '50%',
          padding: '10px',
          cursor: 'pointer',
          zIndex: 10, // Ensure it's above other content
        }}
      >
        <FaBars style={{ color: '#bbb', fontSize: '19px' }} /> {/* Hamburger icon */}
      </div>
    </div>
  );
};


const feedContainerStyle: React.CSSProperties = {
  flexGrow: 1,
  padding: '20px',
  overflowY: 'auto',  // This ensures the container is vertically scrollable
  height: '100%',
  minHeight: '100dvh',
  backgroundColor: '#000',
  border: 'none',
  touchAction: 'pan-y',  // Allows vertical scroll and swipe
  overscrollBehaviorY: 'contain',  // Prevents pull-to-refresh
  overscrollBehaviorX: 'none',
  overflowAnchor: 'none',  // Prevents scroll jump behavior
  willChange: 'transform',
  contain: 'strict',
};  




export default GalleryMobile;